import axios from 'axios';

const baseURL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL,
  // We use JWT Bearer tokens (not cookies). withCredentials must stay false
  // because the platform ingress returns `Access-Control-Allow-Origin: *` on
  // OPTIONS preflights, which the browser rejects when credentials=true.
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

const ACCESS_KEY = 'studioops_access_token';
const REFRESH_KEY = 'studioops_refresh_token';

let accessToken = null;
let refreshToken = null;

export function setAccessToken(token) {
  accessToken = token || null;
  if (token) localStorage.setItem(ACCESS_KEY, token);
  else localStorage.removeItem(ACCESS_KEY);
}
export function setRefreshToken(token) {
  refreshToken = token || null;
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}
export function getAccessToken() {
  if (accessToken) return accessToken;
  const c = localStorage.getItem(ACCESS_KEY);
  if (c) accessToken = c;
  return accessToken;
}
export function getRefreshToken() {
  if (refreshToken) return refreshToken;
  const c = localStorage.getItem(REFRESH_KEY);
  if (c) refreshToken = c;
  return refreshToken;
}
export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  const activePractice = localStorage.getItem('studioops_active_practice');
  if (activePractice && !config.headers['X-Practice-Id']) config.headers['X-Practice-Id'] = activePractice;
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !String(original.url || '').includes('/api/auth/refresh') &&
      !String(original.url || '').includes('/api/auth/login') &&
      !String(original.url || '').includes('/api/auth/me')
    ) {
      original._retry = true;
      const rt = getRefreshToken();
      if (!rt) {
        clearTokens();
        window.dispatchEvent(new CustomEvent('studioops:auth-expired'));
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject, original });
        });
      }
      isRefreshing = true;
      try {
        const r = await axios.post(`${baseURL}/api/auth/refresh`, { refresh_token: rt });
        const newToken = r.data.access_token;
        setAccessToken(newToken);
        refreshQueue.forEach(({ resolve, original: o }) => {
          o.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(o));
        });
        refreshQueue = [];
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        refreshQueue.forEach(({ reject }) => reject(e));
        refreshQueue = [];
        clearTokens();
        window.dispatchEvent(new CustomEvent('studioops:auth-expired'));
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
